import React, { useRef, useState } from "react";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import WelcomeText from "../../assets/headings/welcome-logo.svg";
import Moon from "../../assets/moon.svg";
import MinionNormal from "../../assets/minion/minion-normal.svg";
import MinionConfused from "../../assets/minion/minion-confused.svg";
import Balloon from "../../assets/minion/balloon.svg";
import VectorBalloon from "../../assets/minion/vector-balloon.png";
import OhYeah from "../../assets/minion/oh-yeah-pop.svg";
import "./HeroScene.scss";

gsap.registerPlugin(ScrollTrigger);
ScrollTrigger.config({ ignoreMobileResize: true });

const VECTOR_PARTICLES = [
  { x: "12%", y: "68%", delay: "0s", duration: "2.4s", drift: "-0.55rem" },
  { x: "78%", y: "62%", delay: "0.35s", duration: "2.7s", drift: "0.7rem" },
  { x: "22%", y: "34%", delay: "0.7s", duration: "2.2s", drift: "0.4rem" },
  { x: "70%", y: "28%", delay: "1.05s", duration: "2.9s", drift: "-0.45rem" },
  { x: "48%", y: "14%", delay: "0.2s", duration: "2.5s", drift: "0.25rem" },
  { x: "8%", y: "48%", delay: "1.4s", duration: "2.6s", drift: "0.6rem" },
  { x: "88%", y: "44%", delay: "0.9s", duration: "2.3s", drift: "-0.7rem" },
  { x: "32%", y: "20%", delay: "1.7s", duration: "2.8s", drift: "0.35rem" },
  { x: "62%", y: "74%", delay: "0.55s", duration: "2.1s", drift: "-0.3rem" },
  { x: "40%", y: "8%", delay: "1.2s", duration: "3s", drift: "0.5rem" },
  { x: "18%", y: "58%", delay: "1.85s", duration: "2.45s", drift: "-0.4rem" },
  { x: "84%", y: "22%", delay: "0.15s", duration: "2.65s", drift: "0.55rem" },
];

export function HeroScene() {
  const sceneRef = useRef<HTMLElement>(null);
  const normalRef = useRef<HTMLImageElement>(null);
  const confusedRef = useRef<HTMLImageElement>(null);
  const balloonRef = useRef<HTMLImageElement>(null);
  const vectorBalloonRef = useRef<HTMLDivElement>(null);
  const ohYeahRef = useRef<HTMLImageElement>(null);
  const poppedRef = useRef(false);
  const [popped, setPopped] = useState(false);

  useGSAP(
    () => {
      gsap.set(confusedRef.current, { autoAlpha: 0 });
      gsap.set(vectorBalloonRef.current, { autoAlpha: 0 });
      gsap.set(ohYeahRef.current, {
        autoAlpha: 0,
        scale: 0.28,
        transformOrigin: "50% 50%",
      });

      const media = gsap.matchMedia();

      media.add("(min-width: 768px) and (prefers-reduced-motion: no-preference)", () => {
        gsap.to(".hero-minion-stage", {
          yPercent: -16,
          ease: "none",
          scrollTrigger: {
            trigger: ".hero-top-panel",
            start: "top bottom",
            end: "bottom top",
            scrub: 0.8,
          },
        });
      });

      return () => media.revert();
    },
    { scope: sceneRef }
  );

  const popBalloon = () => {
    if (poppedRef.current) {
      return;
    }

    const balloon = balloonRef.current;
    const vectorBalloon = vectorBalloonRef.current;
    const ohYeah = ohYeahRef.current;
    const normal = normalRef.current;
    const confused = confusedRef.current;
    if (!balloon || !vectorBalloon || !ohYeah || !normal || !confused) {
      return;
    }

    poppedRef.current = true;
    setPopped(true);

    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduced) {
      gsap.set(ohYeah, { autoAlpha: 0, scale: 0.28 });
      gsap.set(balloon, { autoAlpha: 0 });
      gsap.set(vectorBalloon, { autoAlpha: 1 });
      gsap.set(confused, { autoAlpha: 1 });
      gsap.set(normal, { autoAlpha: 0 });
      return;
    }

    gsap
      .timeline()
      .to(ohYeah, {
        autoAlpha: 1,
        scale: 1,
        duration: 0.55,
        ease: "back.out(2.6)",
      }, 0)
      .to(confused, { autoAlpha: 1, duration: 0.18 }, 0)
      .to(normal, { autoAlpha: 0, duration: 0.18 }, 0)
      .to(balloon, { autoAlpha: 0, duration: 0.16, ease: "power1.in" }, 0.22)
      .to(vectorBalloon, { autoAlpha: 1, duration: 0.16 }, 0.22)
      .to(ohYeah, {
        autoAlpha: 0,
        scale: 1.12,
        duration: 0.28,
        ease: "power2.in",
      }, "+=0.28");
  };

  return (
    <section className="hero-scroll-track" id="home" ref={sceneRef}>
      <div className="hero-top-panel">
        <div className="hero-moon-content">
          <img className="hero-moon" src={Moon} alt="" aria-hidden="true" />
          <div className="hero-copy">
            <img className="welcome-text" src={WelcomeText} alt="Welcome to CSCI 0150" />
            <p className="main-description">
              CSCI 0150 is one of the introductory Computer Science courses offered at Brown University.
              <br /><br />
              This course introduces students to Computer Science through object-oriented design and programming,
              using Java and the JavaFX graphics library. You will use these tools for building interactive programs
              with graphical user interfaces. CSCI 0150 reinforces concepts with practical exercises in weekly lab
              sessions and with challenging and engaging programming assignments, such as
              <span className="doodle-color-text"> Doodle Jump</span> and <span className="tetris-color-text">Tetris</span>!
              <br /><br />
              There are no prerequisites for CSCI 0150 and the course expects no prior programming experience.
            </p>
          </div>
        </div>
        <div className="hero-minion-float">
          <div className="hero-minion-stage">
            <img
              className="hero-minion-body hero-minion-body--normal"
              ref={normalRef}
              src={MinionNormal}
              alt=""
            />
            <img
              className="hero-minion-body hero-minion-body--confused"
              ref={confusedRef}
              src={MinionConfused}
              alt=""
            />
            <button
              aria-hidden={popped}
              aria-label="Pop the minion's balloon"
              className={`hero-balloon-slot${popped ? " is-popped" : ""}`}
              onClick={popBalloon}
              tabIndex={popped ? -1 : 0}
              type="button"
            >
              <img
                className="hero-balloon"
                ref={balloonRef}
                src={Balloon}
                alt=""
              />
              <div className="hero-vector-stack" ref={vectorBalloonRef}>
                <span className="hero-vector-aura" aria-hidden="true" />
                <img
                  className="hero-vector-balloon"
                  src={VectorBalloon}
                  alt=""
                />
                <span className="hero-vector-particles" aria-hidden="true">
                  {VECTOR_PARTICLES.map((particle, index) => (
                    <span
                      className={`hero-vector-particle${index % 3 === 0 ? " is-spark" : ""}`}
                      key={index}
                      style={
                        {
                          "--x": particle.x,
                          "--y": particle.y,
                          "--delay": particle.delay,
                          "--duration": particle.duration,
                          "--drift": particle.drift,
                        } as React.CSSProperties
                      }
                    />
                  ))}
                </span>
              </div>
              <span className="hero-oh-yeah-wrap">
                <img
                  className="hero-oh-yeah"
                  ref={ohYeahRef}
                  src={OhYeah}
                  alt=""
                />
              </span>
            </button>
          </div>
        </div>
      </div>
    </section>
  );
}
